import { sharedDocumentFieldsSchema } from "./document";
import { themeType } from "./global";
import { addDays } from "date-fns";
import { z } from "zod";

export const invoiceStatusEnum = z.enum([
  "draft",
  "not-sent",
  "sent",
  "overdue",
  "partially-paid",
  "paid",
  "cancelled",
]);
export type InvoiceStatus = z.infer<typeof invoiceStatusEnum>;

export const baseInvoiceSchema = sharedDocumentFieldsSchema.merge(themeType).extend({
  invoiceNumber: z.string().min(1, { message: "Invoice number is required" }),
  estimateId: z.string().optional().nullable(),
  worklogIds: z.array(z.string()).optional().nullable(),
  status: invoiceStatusEnum.default("draft").optional(),
  dueDate: z.coerce.date({ invalid_type_error: "Due date must be a valid date" }),
  showSwissQrBill: z.boolean().default(false).optional().nullable(),
  projectId: z.string().optional().nullable(),
  organizationBankAccountId: z.string().optional().nullable(),
  organizationBankAccountName: z.string().optional().nullable(),
  organizationBankAccountCountry: z.string().optional().nullable(),
  organizationBankAccountCurrency: z.string().optional().nullable(),
  organizationBankAccountIban: z.string().optional().nullable(),
});

export const createInvoiceSchema = baseInvoiceSchema.refine(
  (data) => {
    if (data.showSwissQrBill) {
      return (
        data.organizationBankAccountId &&
        data.organizationBankAccountName &&
        data.organizationBankAccountCountry &&
        data.organizationBankAccountCurrency &&
        data.organizationBankAccountIban
      );
    }
    return true;
  },
  {
    message: "Bank account required",
    path: ["organizationBankAccountId"],
  },
);

export const editInvoiceSchema = baseInvoiceSchema
  .extend({
    id: z.string(),
  })
  .refine(
    (data) => {
      if (data.showSwissQrBill) {
        return (
          data.organizationBankAccountId &&
          data.organizationBankAccountName &&
          data.organizationBankAccountCountry &&
          data.organizationBankAccountCurrency &&
          data.organizationBankAccountIban
        );
      }
      return true;
    },
    {
      message: "Bank account required",
      path: ["organizationBankAccountId"],
    },
  );

export const getInvoiceSchema = z.object({
  id: z.string(),
});

export const EditInvoicePageSchema = z.object({ id: z.string() });

export const updateInvoiceStatusSchema = z.object({
  status: invoiceStatusEnum,
});

export const sendInvoiceReminderSchema = z.object({
  to: z.string().email("Please enter a valid email address"),
  cc: z.string().email("Please enter a valid email address").optional().nullable(),
  bcc: z.string().email("Please enter a valid email address").optional().nullable(),
  subject: z.string().optional().nullable(),
  content: z.string().optional().nullable(),
});

export type ZodSendInvoiceReminderSchema = z.infer<typeof sendInvoiceReminderSchema>;
export const getInvoicesParamsSchema = z.object({
  // Entity filters
  relationId: z.string().optional(),
  estimateId: z.string().optional(),
  creditNoteId: z.string().optional(),
  projectId: z.string().optional(),
  // Text search filters
  invoiceNumber: z.string().optional(),
  client: z.string().optional(),
  // Status filter
  status: z
    .union([invoiceStatusEnum, z.array(invoiceStatusEnum)])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  // Date range filters
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
  dueDateFrom: z.coerce.date().optional(),
  dueDateTo: z.coerce.date().optional(),
  createdAtFrom: z.coerce.date().optional(),
  createdAtTo: z.coerce.date().optional(),
  paymentDateFrom: z.coerce.date().optional(),
  paymentDateTo: z.coerce.date().optional(),
});

/**
 * Page-based list used by the dashboard Invoices table: the same filters plus
 * a free search (number, client or amount), an amount range, a sort column
 * and page / pageSize. Page is 0-based.
 */
export const invoiceSortColumns = [
  "invoiceNumber",
  "client",
  "status",
  "date",
  "dueDate",
  "total",
  "createdAt",
] as const;

export const getInvoicesPageParamsSchema = getInvoicesParamsSchema.extend({
  search: z.string().trim().max(100).optional(),
  totalMin: z.coerce.number().optional(),
  totalMax: z.coerce.number().optional(),
  sortBy: z.enum(invoiceSortColumns).optional(),
  sortDir: z.enum(["asc", "desc"]).optional(),
  page: z.coerce.number().int().min(0).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).optional(),
});

export type ZodGetInvoicesPageParams = z.input<typeof getInvoicesPageParamsSchema>;
export type ZodGetInvoicesPageQuery = z.infer<typeof getInvoicesPageParamsSchema>;
export type InvoiceSortColumn = (typeof invoiceSortColumns)[number];

/** Header figures of the Invoices page, in the organization currency. */
export interface InvoiceStatsSchema {
  currency: string;
  outstanding: { amount: number; count: number };
  overdue: { amount: number; count: number; oldestDays: number };
  paidThisMonth: { amount: number; count: number };
  drafts: { amount: number; count: number };
}

export type ZodCreateInvoiceSchema = z.infer<typeof createInvoiceSchema>;
export type ZodEditInvoiceSchema = z.infer<typeof editInvoiceSchema>;
export type ZodGetInvoicesParams = z.infer<typeof getInvoicesParamsSchema>;

export const createInvoiceSchemaDefaultValues: ZodCreateInvoiceSchema = {
  estimateId: undefined,
  worklogIds: undefined,
  status: "draft",

  organizationName: "",
  organizationLogo: "",
  organizationAddressLine1: "",
  organizationAddressLine2: "",
  organizationCity: "",
  organizationState: "",
  organizationZipcode: "",
  organizationCountry: "",

  relationId: "",
  relationName: "",
  relationAddressLine1: "",
  relationAddressLine2: "",
  relationCity: "",
  relationState: "",
  relationZipcode: "",
  relationCountry: "",

  model: "default",
  language: "en",
  primaryColor: "#635CFF",
  textBeforeItems: "",
  textAfterItems: "",
  footer: "",

  currency: "USD",
  exchangeRates: null,
  invoiceNumber: "",
  date: new Date(),
  dueDate: addDays(new Date(), 30),
  showItemsWithTax: false,
  showSwissQrBill: false,
  total: 0,
  subtotal: 0,

  organizationBankAccountId: undefined,
  organizationBankAccountName: undefined,
  organizationBankAccountCountry: undefined,
  organizationBankAccountCurrency: undefined,
  organizationBankAccountIban: undefined,

  items: [],
  taxes: [],
};

export const invoiceTypeEnum = z.enum(["local", "server"]);
export const invoiceValueTypesEnum = z.enum(["fixed", "percentage"]);

export type InvoiceType = z.infer<typeof invoiceTypeEnum>;
export type InvoiceValueType = z.infer<typeof invoiceValueTypesEnum>;

export const invoiceSchema = baseInvoiceSchema.extend({
  id: z.string(),
  invoiceReference: z.number().optional().nullable(),
  paidAt: z.date().nullable(),
  paymentDate: z.date().nullable(),
  totalPaid: z.number().default(0).optional().nullable(),
  sentDate: z.date().optional().nullable(),
  openDate: z.date().optional().nullable(),
  cancelDate: z.date().optional().nullable(),
  lastReminderSentAt: z.date().optional().nullable(),
  reminderCount: z.number().int().nonnegative().default(0).optional(),
  organizationId: z.string(),
  userId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type InvoiceSchema = z.infer<typeof invoiceSchema>;
